import { defineComponent, computed, PropType } from 'vue';
import {
  type CurrencySettings,
  type CashierActionType,
  type InnerTableRowMethodProps,
  CurrencyTableRowSpan,
  currencySettingsColumns,
  CurrencyTableColumnProperties,
  CashierActionTypeTitle,
} from '@/models/cashier-settings/currency-table.model';
import type { CurrencyItem } from '@/models/dictionary/currency-list.model';
import type {
  CashierConfig,
  CashierConfigDefaultAmountItem,
  CashierConfigSuggestedAmountItem,
} from '@/models/cashier-settings/cashier-settings.model';

export default defineComponent({
  name: 'CurrencyTable',
  props: {
    row: {
      type: Object as PropType<CashierConfig>,
      required: true,
    },
  },
  setup(props) {
    const COLLAPSE_COLUMN_INDEXES = [0, 1, 4];

    const subsequentCurrencyList = computed<Set<string>>(() => {
      const list = (props.row.subsequentAmount?.depositList ?? []).map(
        (amountItem: CashierConfigDefaultAmountItem) => amountItem.currency,
      );

      return new Set(list);
    });

    const rowSpanList = computed<number[]>(() => {
      const allCurrencyList: string[] = currencySettingsList.value.map(
        (settings: CurrencySettings) => settings.currency,
      );

      const uniqueCurrencyList: string[] = Array.from(new Set(allCurrencyList));

      const perRow: number[] = uniqueCurrencyList.flatMap((currency: string) => {
        const span: CurrencyTableRowSpan = subsequentCurrencyList.value.has(currency)
          ? CurrencyTableRowSpan.THREE
          : CurrencyTableRowSpan.TWO;

        return [span, ...Array(span - 1).fill(0)];
      });

      return perRow;
    });

    const spanMethod = ({ rowIndex, columnIndex }: InnerTableRowMethodProps): number[] | undefined => {
      if (!COLLAPSE_COLUMN_INDEXES.includes(columnIndex)) {
        return;
      }

      const span = rowSpanList.value[rowIndex];

      return span && span > 0 ? [span, 1] : [0, 0];
    };

    const currencySettingsList = computed<CurrencySettings[]>(() => {
      const config = props.row;
      const map: Record<CurrencyItem['code'], CurrencySettings> = {};

      const ensureEntry = (currency: CurrencyItem['code'], type: CashierActionType): CurrencySettings => {
        const key = `${type}-${currency}`;
        if (!map[key]) {
          map[key] = { currency, type };
        }

        return map[key];
      };

      const addDefaultAmounts = (
        cashierConfigDefaultAmountItem: CashierConfigDefaultAmountItem[] | undefined,
        type: CashierActionType,
      ): void => {
        cashierConfigDefaultAmountItem?.forEach(({ currency, amount }: CashierConfigDefaultAmountItem) => {
          ensureEntry(currency, type).defaultAmount = amount;
        });
      };

      const addSuggestedAmounts = (
        cashierConfigSuggestedAmountItem: CashierConfigSuggestedAmountItem[] | undefined,
        type: CashierActionType,
      ): void => {
        cashierConfigSuggestedAmountItem?.forEach((
          { currency, presetAmountList }: CashierConfigSuggestedAmountItem,
        ) => {
          ensureEntry(currency, type).presetAmountList = presetAmountList;
        });
      };

      // Добавляем данные по defaultAmount
      addDefaultAmounts(config.defaultAmount?.depositList, 'deposit');
      addDefaultAmounts(config.subsequentAmount?.depositList, 'subsequentAmount');
      addDefaultAmounts(config.defaultAmount?.withdrawalList, 'withdrawal');

      // Добавляем данные по suggestedAmount
      addSuggestedAmounts(config.suggestedAmount?.depositList, 'deposit');
      addSuggestedAmounts(config.suggestedAmount?.withdrawalList, 'withdrawal');

      const currencySettingListMappedValueList: CurrencySettings[] = Object.values(map);

      // Собираем все валюты, которые есть в map
      const allCurrencyListFromMappedValueList = currencySettingListMappedValueList
        .map((currencySettings: CurrencySettings) => currencySettings.currency);

      // Гарантируем, что для каждой валюты есть оба типа (deposit и withdrawal)
      allCurrencyListFromMappedValueList.forEach((currency: string) => {
        ensureEntry(currency, 'deposit');
        ensureEntry(currency, 'withdrawal');
      });

      return currencySettingListMappedValueList
        .map((originalItem: CurrencySettings) => {
          const item = { ...originalItem };

          // Устанавливаем defaultAmount и presetAmountList, если они не заданы
          item.defaultAmount ??= null;
          item.presetAmountList ??= [];

          return item;
        })
        .sort((a, b) => a.currency.localeCompare(b.currency));
    });

    return {
      spanMethod,
      currencySettingsList,
      currencySettingsColumns,
      CurrencyTableColumnProperties,
      CashierActionTypeTitle,
    };
  },
});
